// src/pages/orders/2-Periods-Product-Sales.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import { Card } from "../../components/card/card.jsx";

import {
  fetchAnalyticsProductOptions,
  fetchOrdersProductComparisonByUpcDateRanges,
} from "../../actions/orders_actions.js";

/** ----------------- helpers ----------------- */
const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeChartType = (v) =>
  String(v || "").toLowerCase() === "line" ? "line" : "bar";

const withDotThousands = (digits) =>
  String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const formatPercent0 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(0)}%`;
};

const normalizeCssColorToHex = (input, fallback = "#0d6efd") => {
  const v = String(input || "").trim();
  if (!v) return fallback;

  // Resolve var(--foo)
  if (/^var\(/i.test(v) && typeof document !== "undefined") {
    const m = v.match(/^var\(\s*(--[^,\s)]+)/i);
    if (m?.[1]) {
      const resolved = getComputedStyle(document.body)
        .getPropertyValue(m[1])
        .trim();
      return normalizeCssColorToHex(resolved, fallback);
    }
  }

  // #RGB or #RRGGBB
  if (v.startsWith("#")) {
    const hex = v.slice(1);
    if (/^[0-9a-f]{3}$/i.test(hex)) {
      return `#${hex
        .split("")
        .map((c) => c + c)
        .join("")}`.toLowerCase();
    }
    if (/^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`.toLowerCase();
    return fallback;
  }

  // rgb(...) / rgba(...)
  const rgb = v.match(/rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i);
  if (rgb) {
    const r = Math.max(0, Math.min(255, Math.round(Number(rgb[1]))));
    const g = Math.max(0, Math.min(255, Math.round(Number(rgb[2]))));
    const b = Math.max(0, Math.min(255, Math.round(Number(rgb[3]))));
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  // "13, 110, 253"
  const triplet = v.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
  if (triplet) {
    const r = Math.max(0, Math.min(255, Number(triplet[1])));
    const g = Math.max(0, Math.min(255, Number(triplet[2])));
    const b = Math.max(0, Math.min(255, Number(triplet[3])));
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  return fallback;
};

const getThemeColorFromCss = () =>
  normalizeCssColorToHex(
    typeof document !== "undefined"
      ? getComputedStyle(document.body).getPropertyValue("--bs-theme").trim()
      : "",
    "#0d6efd"
  );

const formatCurrencyCompact = (value, currencySymbol = "$") => {
  const n = safeNumber(value);
  const isNeg = n < 0;
  const intAbs = Math.trunc(Math.abs(n));
  return `${isNeg ? "-" : ""}${currencySymbol}${withDotThousands(intAbs)}`;
};

// --- lighter/darker SAME-HUE variant (no hue shift) ---
const hexToRgb = (hex) => {
  const h = String(hex || "").replace(/^#/, "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const luminance01 = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  const srgb = [rgb.r, rgb.g, rgb.b].map((v) => v / 255);
  const lin = srgb.map((c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};

const lighten = (hex, amt) => {
  const rgb = hexToRgb(normalizeCssColorToHex(hex, "#0d6efd"));
  if (!rgb) return "#0d6efd";
  const a = Math.max(0, Math.min(1, Number(amt) || 0));
  const r = Math.round(rgb.r + (255 - rgb.r) * a);
  const g = Math.round(rgb.g + (255 - rgb.g) * a);
  const b = Math.round(rgb.b + (255 - rgb.b) * a);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
};

const darken = (hex, amt) => {
  const rgb = hexToRgb(normalizeCssColorToHex(hex, "#0d6efd"));
  if (!rgb) return "#0d6efd";
  const a = Math.max(0, Math.min(1, Number(amt) || 0));
  const r = Math.round(rgb.r * (1 - a));
  const g = Math.round(rgb.g * (1 - a));
  const b = Math.round(rgb.b * (1 - a));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
};

const makeSecondSameHue = (baseHex) => {
  const base = normalizeCssColorToHex(baseHex, "#0d6efd");
  const lum = luminance01(base);
  if (lum > 0.55) return darken(base, 0.38);
  return lighten(base, 0.62);
};

// Chart label colors (dark theme)
const CHART_LABEL = "#e9ecef";
const CHART_MUTED = "#adb5bd";
const CHART_GRID = "rgba(255,255,255,0.12)";
const CHART_TOOLTIP_THEME = "dark";

const ChartTypeSelect = ({ value, onChange }) => (
  <div className="d-flex align-items-center gap-2">
    <span className="small text-muted">Chart:</span>
    <select
      className="form-select form-select-sm w-auto"
      value={value}
      onChange={(e) => onChange(normalizeChartType(e.target.value))}
      title="Select chart type"
    >
      <option value="bar">Bar</option>
      <option value="line">Line</option>
    </select>
  </div>
);
/** ------------------------------------------ */

const ProductSalesCompare = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [themeColor, setThemeColor] = useState(getThemeColorFromCss);
  useEffect(() => {
    const update = () => setThemeColor(getThemeColorFromCss());
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "style"] });
    return () => obs.disconnect();
  }, []);

  const secondColor = useMemo(() => makeSecondSameHue(themeColor), [themeColor]);

  const hydratedRef = useRef(false);

  const lastFetchKeyRef = useRef("");


  const [chartTypeQty, setChartTypeQty] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("qtyChartType");
    return normalizeChartType(p || "bar");
  });

  const [chartTypeAmount, setChartTypeAmount] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("amountChartType");
    return normalizeChartType(p || "bar");
  });


  // Draft filters
  const [p1FromDraft, setP1FromDraft] = useState(
    () => new URLSearchParams(window.location.search).get("p1From") || "2025-12-01"
  );
  const [p1ToDraft, setP1ToDraft] = useState(
    () => new URLSearchParams(window.location.search).get("p1To") || "2025-12-31"
  );
  const [p2FromDraft, setP2FromDraft] = useState(
    () => new URLSearchParams(window.location.search).get("p2From") || "2026-01-01"
  );
  const [p2ToDraft, setP2ToDraft] = useState(
    () => new URLSearchParams(window.location.search).get("p2To") || "2026-01-31"
  );

  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  // Applied filters
  const [p1From, setP1From] = useState(p1FromDraft);
  const [p1To, setP1To] = useState(p1ToDraft);
  const [p2From, setP2From] = useState(p2FromDraft);
  const [p2To, setP2To] = useState(p2ToDraft);
  const [appliedProductIds, setAppliedProductIds] = useState([]);

  const {
    analytics_product_options,
    loading_analytics_product_options,
    analytics_product_options_error,
  } = useSelector((state) => ({
    analytics_product_options: state.orders?.analytics_product_options || [],
    loading_analytics_product_options: state.orders?.loading_analytics_product_options,
    analytics_product_options_error: state.orders?.analytics_product_options_error,
  }));

  const {
    orders_product_comparison,
    loading_orders_product_comparison,
    orders_product_comparison_error,
  } = useSelector((state) => ({
    orders_product_comparison: state.orders?.orders_product_comparison,
    loading_orders_product_comparison: state.orders?.loading_orders_product_comparison,
    orders_product_comparison_error: state.orders?.orders_product_comparison_error,
  }));

  useEffect(() => {
    dispatch(fetchAnalyticsProductOptions());
  }, [dispatch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.("#productsMultiSelectDropdown")) setProductsDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const normalizeIdsKey = useCallback(
    (ids) =>
      (Array.isArray(ids) ? ids : [])
        .map((x) => String(x).trim())
        .filter(Boolean)
        .sort()
        .join(","),
    []
  );

  const toggleProductId = useCallback((id) => {
    const sid = String(id);
    setSelectedProductIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  }, []);

  const clearSelectedProducts = useCallback(() => setSelectedProductIds([]), []);

  const selectedProductsLabel = useMemo(() => {
    if (!selectedProductIds.length) return "All products";
    const labelById = new Map((analytics_product_options || []).map((o) => [String(o.value), o.label]));
    const labels = selectedProductIds.map((id) => labelById.get(String(id)) || id);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  }, [selectedProductIds, analytics_product_options]);

  const selectedProductObjects = useMemo(() => {
    const byId = new Map((analytics_product_options || []).map((o) => [String(o.value), o]));
    return (appliedProductIds || []).map((id) => byId.get(String(id))).filter(Boolean);
  }, [appliedProductIds, analytics_product_options]);

  const appliedUpcsForUS = useMemo(() => {
    const upcs = selectedProductObjects.flatMap((o) => (Array.isArray(o.upcs) ? o.upcs : []));
    return Array.from(new Set(upcs.map(String).map((s) => s.trim()).filter(Boolean)));
  }, [selectedProductObjects]);

  useEffect(() => {
    const key = [p1From, p1To, p2From, p2To, appliedUpcsForUS.join(",")].join("|");
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;

    dispatch(
      fetchOrdersProductComparisonByUpcDateRanges({
        dateRange1From: p1From,
        dateRange1To: p1To,
        dateRange2From: p2From,
        dateRange2To: p2To,
        upcs: appliedUpcsForUS,
      })
    );
  }, [dispatch, p1From, p1To, p2From, p2To, appliedUpcsForUS]);


  useEffect(() => {
    if (hydratedRef.current) return;

    const params = new URLSearchParams(location.search);
    const productsCsv = params.get("products") || "";
    const productsFromUrl = productsCsv.split(",").map((x) => String(x).trim()).filter(Boolean);

    if (productsFromUrl.length) {
      setSelectedProductIds(productsFromUrl);
      setAppliedProductIds(productsFromUrl);
    }

    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = useCallback(() => {
    setP1From(p1FromDraft);
    setP1To(p1ToDraft);
    setP2From(p2FromDraft);
    setP2To(p2ToDraft);
    setAppliedProductIds(selectedProductIds);
    setProductsDropdownOpen(false);
  }, [p1FromDraft, p1ToDraft, p2FromDraft, p2ToDraft, selectedProductIds]);

  const hasFiltersChanges = useMemo(() => {
    const productsChanged = normalizeIdsKey(selectedProductIds) !== normalizeIdsKey(appliedProductIds);
    const datesChanged =
      p1FromDraft !== p1From || p1ToDraft !== p1To || p2FromDraft !== p2From || p2ToDraft !== p2To;
    return productsChanged || datesChanged;
  }, [
    normalizeIdsKey,
    selectedProductIds,
    appliedProductIds,
    p1FromDraft,
    p1ToDraft,
    p2FromDraft,
    p2ToDraft,
    p1From,
    p1To,
    p2From,
    p2To,
  ]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    const desired = new URLSearchParams();
    desired.set("p1From", p1From);
    desired.set("p1To", p1To);
    desired.set("p2From", p2From);
    desired.set("p2To", p2To);

    desired.set("qtyChartType", chartTypeQty);
    desired.set("amountChartType", chartTypeAmount);

    if (appliedProductIds?.length) desired.set("products", appliedProductIds.map(String).join(","));

    const desiredStr = desired.toString();
    const currentStr = new URLSearchParams(location.search).toString();
    if (currentStr === desiredStr) return;

    //  IMPORTANT: don't hardcode "/orders/product-sales-compare"
    // keep whatever the current route is (e.g. "/orders/product-sales/compare")
    navigate({ pathname: location.pathname, search: `?${desiredStr}` }, { replace: true });
  }, [
    navigate,
    location.pathname,
    location.search,
    p1From, p1To, p2From, p2To,
    chartTypeQty, chartTypeAmount,
    appliedProductIds
  ]);



  const root = orders_product_comparison || {};
  const period1 = root.period1 || {};
  const period2 = root.period2 || {};
  const totals1 = period1.totals || {};
  const totals2 = period2.totals || {};

  const percentChange = root.percentChange || {};
  const pctQty = percentChange.qty;
  const pctTotal = percentChange.total;

  const products = Array.isArray(root.products) ? root.products : [];
  const categories = products.map((p) => p.productName || `#${p.productId}`);

  const seriesTotal = [
    { name: "Period 1", data: products.map((p) => safeNumber(p?.period1?.total)) },
    { name: "Period 2", data: products.map((p) => safeNumber(p?.period2?.total)) },
  ];

  const seriesQty = [
    { name: "Period 1", data: products.map((p) => safeNumber(p?.period1?.qty)) },
    { name: "Period 2", data: products.map((p) => safeNumber(p?.period2?.qty)) },
  ];

  const colors = [themeColor, secondColor];

  const Y_LABEL_WIDTH = 86; // adjust until both plots start at same X (try 80–100)

  const buildChartOpts = useCallback(
    ({ title, yFormatter, isLine }) => {
      const labelColor = secondColor;
      const gridColor  = secondColor;

      return {
        chart: {
          toolbar: { show: false },
          fontFamily: "Chakra Petch, system-ui, -apple-system, BlinkMacSystemFont",
          background: "transparent",
          foreColor: labelColor,
        },

        colors,

        dataLabels: { enabled: false },

        grid: {
          borderColor: gridColor,
          padding: {
            left: 6,   // keep small; main alignment comes from minWidth below
            right: 6,
          },
        },

        xaxis: {
          categories,
          tickPlacement: "on", //  keeps line points & bars centered consistently
          labels: {
            rotate: -15,
            style: {
              fontSize: "12px",
              colors: categories.map(() => labelColor),
            },
          },
          axisBorder: { show: true, color: gridColor },
          axisTicks:  { show: true, color: gridColor },
        },

        yaxis: {
          labels: {
            formatter: yFormatter,
            style: { fontSize: "12px", colors: labelColor },

            //  FORCE SAME LABEL GUTTER WIDTH on BOTH charts
            minWidth: Y_LABEL_WIDTH,
            maxWidth: Y_LABEL_WIDTH,
          },
        },

        legend: {
          show: true,
          position: "bottom",
          labels: { colors: labelColor, useSeriesColors: false },
        },

        tooltip: { theme: "dark", y: { formatter: yFormatter } },

        stroke: isLine ? { width: 3, curve: "smooth" } : { width: 0 },
        markers: isLine ? { size: 3 } : { size: 0 },

        //  keep for bars so bar geometry is predictable
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 2 } },

        title: {
          text: title,
          align: "center",
          style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: labelColor },
        },
      };
    },
    [categories, colors, secondColor]
  );



  const qtyFormatter = useCallback((v) => withDotThousands(String(Math.trunc(safeNumber(v)))), []);
  const amountFormatter = useCallback((v) => formatCurrencyCompact(v, "$"), []);

  const isLineQty = chartTypeQty === "line";
  const isLineAmount = chartTypeAmount === "line";

  const qtyChartOpts = useMemo(
    () => buildChartOpts({ title: "Qty (Period 1 vs Period 2)", yFormatter: qtyFormatter, isLine: isLineQty }),
    [buildChartOpts, qtyFormatter, isLineQty]
  );

  const amountChartOpts = useMemo(
    () => buildChartOpts({ title: "Amount (Period 1 vs Period 2)", yFormatter: amountFormatter, isLine: isLineAmount }),
    [buildChartOpts, amountFormatter, isLineAmount]
  );


  const CONTROL_HEIGHT = 38;
  const CONTROL_RADIUS = 6;

  return (
    <div>
      {/* Header */}
      <div className="mb-3">
        <div className="row g-3 align-items-start align-items-xl-center">
          <div className="col-12 col-xl-auto">
            <div>
              <ul className="breadcrumb">
                <h6>
                  <li className="breadcrumb-item active">PAGES /</li>
                </h6>
                <h6>
                  <li className="breadcrumb-item">&nbsp;PRODUCT SALES COMPARISON</li>
                </h6>
              </ul>
              <h1 className="page-header mb-0">PRODUCT SALES COMPARISON</h1>
            </div>
          </div>
        </div>
      </div>

      {/* ALL FILTERS IN ONE CARD */}
      {/*  IMPORTANT: position + zIndex here fixes "menu behind next card" even if Card CSS creates stacking contexts */}
      <Card className="mb-4" style={{ overflow: "visible", position: "relative", zIndex: 12000 }}>
        <div className="px-4 pt-3 pb-3" style={{ overflow: "visible" }}>
          <div className="row g-3 align-items-end">
            {/* Products */}
            <div className="col-12 col-xl-5">
              <div
                id="productsMultiSelectDropdown"
                className="position-relative"
                style={{
                  overflow: "visible",
                  zIndex: productsDropdownOpen ? 13000 : "auto",
                }}
              >
                <label className="mb-1 fw-bold">Products</label>

                <div className="dropdown" style={{ minWidth: 0 }}>
                  <button
                    type="button"
                    className="form-control text-start d-flex align-items-center justify-content-between"
                    onClick={() => setProductsDropdownOpen((v) => !v)}
                    disabled={loading_analytics_product_options}
                    aria-expanded={productsDropdownOpen}
                    title="Select one or more products"
                    style={{
                      cursor: "pointer",
                      height: CONTROL_HEIGHT,
                      minHeight: CONTROL_HEIGHT,
                      borderRadius: CONTROL_RADIUS,
                    }}
                  >
                    <span className="text-truncate flex-grow-1" style={{ minWidth: 0 }}>
                      {loading_analytics_product_options ? "Loading…" : selectedProductsLabel}
                    </span>
                    <i className={`fa fa-chevron-${productsDropdownOpen ? "up" : "down"} ms-2 flex-shrink-0`} />
                  </button>

                  {productsDropdownOpen && (
                    <div
                      className="dropdown-menu show p-2"
                      style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        left: 0,
                        right: 0,
                        width: "100%",
                        maxHeight: 280,
                        overflowY: "auto",
                        zIndex: 14000,
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between px-2 pb-2">
                        <div className="small text-muted">
                          {selectedProductIds.length ? `${selectedProductIds.length} selected` : "None selected"}
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearSelectedProducts();
                          }}
                          disabled={!selectedProductIds.length}
                        >
                          Clear
                        </button>
                      </div>

                      <div className="dropdown-divider my-1" />

                      {analytics_product_options_error ? (
                        <div className="text-danger small px-2 py-2">{analytics_product_options_error}</div>
                      ) : (analytics_product_options || []).length === 0 ? (
                        <div className="text-muted small px-2 py-2">No products found</div>
                      ) : (
                        (analytics_product_options || []).map((opt) => {
                          const id = String(opt.value);
                          const checked = selectedProductIds.includes(id);

                          return (
                            <div
                              key={id}
                              role="menuitemcheckbox"
                              aria-checked={checked}
                              className="dropdown-item d-flex align-items-center gap-2"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleProductId(id);
                              }}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 16,
                                  height: 16,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 3,
                                  border: `1px solid ${checked ? themeColor : "rgba(255,255,255,0.35)"}`,
                                  background: checked ? themeColor : "transparent",
                                  boxShadow: checked ? `0 0 0 2px ${themeColor}33` : "none",
                                  flex: "0 0 auto",
                                }}
                              >
                                {checked ? (
                                  <i className="fa fa-check" style={{ color: "#fff", fontSize: 11, lineHeight: 1 }} />
                                ) : null}
                              </span>

                              <span className="flex-grow-1">{opt.label}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Period 1 */}
            <div className="col-12 col-md-6 col-xl-3">
              <label className="mb-1 fw-bold">Period 1</label>
              <div className="d-flex align-items-center gap-2">
                <input className="form-control" type="date" value={p1FromDraft} onChange={(e) => setP1FromDraft(e.target.value)} />
                <span className="text-muted">to</span>
                <input className="form-control" type="date" value={p1ToDraft} onChange={(e) => setP1ToDraft(e.target.value)} />
              </div>
            </div>

            {/* Period 2 */}
            <div className="col-12 col-md-6 col-xl-3">
              <label className="mb-1 fw-bold">Period 2</label>
              <div className="d-flex align-items-center gap-2">
                <input className="form-control" type="date" value={p2FromDraft} onChange={(e) => setP2FromDraft(e.target.value)} />
                <span className="text-muted">to</span>
                <input className="form-control" type="date" value={p2ToDraft} onChange={(e) => setP2ToDraft(e.target.value)} />
              </div>
            </div>

            {/* Apply (smaller magnifier) */}
            <div className="col-12 col-xl-1 d-flex flex-column">
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-2"
                onClick={applyFilters}
                disabled={!hasFiltersChanges}
                title={hasFiltersChanges ? "Apply filters" : "No changes to apply"}
                style={{
                  height: 34,
                  width: 38,
                  padding: 0,
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fas fa-search" />
              </button>
            </div>
          </div>

          {hasFiltersChanges ? <div className="small text-warning mt-2">Filters changed — press Apply</div> : null}
        </div>
      </Card>

      {/* Totals */}
      <Card className="mb-4">
        <div className="px-4 pt-3 pb-2">
          <h4 className="mb-1">Totals</h4>
          <div className="text-muted small">
            Period 1: {p1From} → {p1To} &nbsp;|&nbsp; Period 2: {p2From} → {p2To}
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="row text-center">
            <div className="col-md-3 mb-3">
              <div className="fw-bold" style={{ fontSize: 22, color: themeColor }}>
                {withDotThousands(String(Math.trunc(safeNumber(totals1.qty))))}
              </div>
              <div className="small" style={{ color: themeColor, opacity: 0.9 }}>
                Period 1 Qty
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <div className="fw-bold" style={{ fontSize: 22, color: themeColor }}>
                {formatCurrencyCompact(totals1.total, "$")}
              </div>
              <div className="small" style={{ color: themeColor, opacity: 0.9 }}>
                Period 1 Amount
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <div className="fw-bold" style={{ fontSize: 22, color: secondColor }}>
                {withDotThousands(String(Math.trunc(safeNumber(totals2.qty))))}
              </div>
              <div className="small" style={{ color: secondColor, opacity: 0.9 }}>
                Period 2 Qty
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <div className="fw-bold" style={{ fontSize: 22, color: secondColor }}>
                {formatCurrencyCompact(totals2.total, "$")}
              </div>
              <div className="small" style={{ color: secondColor, opacity: 0.9 }}>
                Period 2 Amount
              </div>
            </div>
          </div>

          <div className="row text-center mt-2">
            <div className="col-md-6 mb-2">
              <div className="fw-bold" style={{ fontSize: 16 }}>
                Qty % Change: {formatPercent0(pctQty)}
              </div>
              <div className="text-muted small">(Period 1 vs Period 2)</div>
            </div>
            <div className="col-md-6 mb-2">
              <div className="fw-bold" style={{ fontSize: 16 }}>
                Amount % Change: {formatPercent0(pctTotal)}
              </div>
              <div className="text-muted small">(Period 1 vs Period 2)</div>
            </div>
          </div>
        </div>
      </Card>

      {/* TWO GRAPHS: Qty + Amount */}
      <div className="row g-4">
        <div className="col-12 col-xl-6">
          <Card>
            <div className="d-flex flex-wrap justify-content-between align-items-center px-4 pt-3 gap-2">
              <div>
                <h4 className="mb-1">Product Sales Comparison</h4>
                <div className="text-muted small">Two-period comparison</div>
              </div>
              <ChartTypeSelect value={chartTypeQty} onChange={setChartTypeQty} />
            </div>

            <div className="px-4 pb-4">
              {loading_orders_product_comparison ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : orders_product_comparison_error ? (
                <div className="text-danger text-center py-5">{orders_product_comparison_error}</div>
              ) : products.length === 0 ? (
                <div className="text-center py-5">No data for this comparison.</div>
              ) : (
                <Chart options={qtyChartOpts} series={seriesQty} type={chartTypeQty} height={320} />
              )}
            </div>
          </Card>
        </div>

        <div className="col-12 col-xl-6">
          <Card>
            <div className="d-flex flex-wrap justify-content-between align-items-center px-4 pt-3 gap-2">
              <div>
                <h4 className="mb-1">Product Sales Comparison</h4>
                <div className="text-muted small">Two-period comparison</div>
              </div>
              <ChartTypeSelect value={chartTypeAmount} onChange={setChartTypeAmount} />
            </div>

            <div className="px-4 pb-4">
              {loading_orders_product_comparison ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : orders_product_comparison_error ? (
                <div className="text-danger text-center py-5">{orders_product_comparison_error}</div>
              ) : products.length === 0 ? (
                <div className="text-center py-5">No data for this comparison.</div>
              ) : (
                <Chart options={amountChartOpts} series={seriesTotal} type={chartTypeAmount} height={320} />
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProductSalesCompare;
